import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { asDecimalString, APP_ROLES } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createApprovalRule, listApprovalRules } from "../../api/approvalsApi";
import { listPermissions } from "../../api/permissionsApi";
import type { ApprovalRuleOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const COLUMNS: DataTableColumn<ApprovalRuleOut, keyof ApprovalRuleOut>[] = [
  { key: "entity_type", header: "Entity", render: (r) => r.entity_type },
  { key: "min_amount", header: "Threshold (≥)", align: "right", render: (r) => r.min_amount },
  { key: "required_role", header: "Reference role", render: (r) => r.required_role ?? "—" },
  { key: "required_permission_code", header: "Reference permission", render: (r) => r.required_permission_code ?? "—" },
  { key: "is_active", header: "Active", render: (r) => (r.is_active ? "Yes" : "No") },
];

/**
 * Approval Rules configuration — `GET/POST /approval-rules` (§11.2). A Purchase Order
 * whose total (`sum(quantity_ordered * unit_cost)`) meets or exceeds the strictest
 * matching active rule's `min_amount` is created `PENDING_APPROVAL` instead of `ORDERED`
 * and gets a paired entry in the Approval Requests queue. `required_role`/
 * `required_permission_code` are recorded for reference only — they do NOT currently gate
 * who can decide (that's hardcoded server-side to the `purchase_order.approve`
 * permission), so this form labels them as informational, not enforcement.
 *
 * Gated by `require_permission("approval_rule.manage")` — same reachability note as
 * `ApprovalRequestsPage`.
 */
export function ApprovalRulesPage() {
  const { ensureFreshToken } = useAuth();
  const rulesState = useAsyncData(() => ensureFreshToken().then((token) => listApprovalRules(token)), []);
  const permissionsState = useAsyncData(() => ensureFreshToken().then((token) => listPermissions(token)), []);

  const [minAmount, setMinAmount] = useState("");
  const [requiredRole, setRequiredRole] = useState("");
  const [requiredPermissionCode, setRequiredPermissionCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setForbidden(false);
    if (!minAmount) {
      setFormError("Enter a threshold amount.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createApprovalRule(token, {
        entity_type: "PURCHASE_ORDER",
        min_amount: asDecimalString(minAmount),
        required_role: requiredRole || undefined,
        required_permission_code: requiredPermissionCode || undefined,
      });
      setMinAmount("");
      setRequiredRole("");
      setRequiredPermissionCode("");
      rulesState.reload();
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) setForbidden(true);
      else setFormError(error instanceof ApiError ? error.message : "Could not create rule.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Approval Rule Thresholds</h1>
          <p className="page__subtitle">
            A new Purchase Order whose total meets or exceeds the strictest matching rule's threshold requires
            approval before it can be received.
          </p>
        </div>
        <Link to="/approvals" className="sl-button sl-button--secondary sl-button--md">
          Review queue
        </Link>
      </div>

      {forbidden ? (
        <StatusBanner kind="info">
          Your role doesn't currently have the <code>approval_rule.manage</code> permission (platform default: OWNER
          only).
        </StatusBanner>
      ) : null}
      {rulesState.error ? <StatusBanner kind="error">{rulesState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New threshold rule</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input
              label="Minimum PO total (currency units)"
              inputMode="decimal"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="e.g. 5000.00"
              required
            />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="required-role">
                Reference role (informational only)
              </label>
              <select id="required-role" className="sl-input" value={requiredRole} onChange={(e) => setRequiredRole(e.target.value)}>
                <option value="">None</option>
                {APP_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="required-permission">
                Reference permission (informational only)
              </label>
              <select
                id="required-permission"
                className="sl-input"
                value={requiredPermissionCode}
                onChange={(e) => setRequiredPermissionCode(e.target.value)}
              >
                <option value="">None</option>
                {(permissionsState.data ?? []).map((p) => (
                  <option key={p.id} value={p.code}>
                    {p.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="muted">
            Actual approve/reject access is always gated by the <code>purchase_order.approve</code> permission
            server-side — these two fields are recorded for reference only and don't change enforcement.
          </p>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create rule"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        {rulesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={COLUMNS} rows={rulesState.data ?? []} getRowId={(row) => row.id} emptyMessage="No approval rules configured — every PO goes straight to ORDERED." caption="Approval Rules" />
        )}
      </div>
    </div>
  );
}
